import axios, { isAxiosError, isCancel } from "axios";
import { type EffectCallback, useEffect } from "react";
import { findSupportedFile } from "shared";
import { useEventCallback } from "usehooks-ts";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { useShallow } from "zustand/react/shallow";

type UploadPayload = {
	QUEUED: never;
	REQUESTING: never; // Requesting to upload from the back-end
	UPLOADING: {
		mediaID: number;
		progress: number;
	};
	CONFIRMING: { mediaID: number };
	COMPLETE: { mediaID: number };
	ERROR: { error: string };
};

type UploadState = {
	[K in keyof UploadPayload]: UploadPayload[K] extends never
		? { status: K }
		: { status: K; payload: UploadPayload[K] };
}[keyof UploadPayload];

export type Upload = {
	id: string;
	state: UploadState;
	file: File;
	controller: AbortController;
};

type UploadCompleteListener = (mediaID: number) => unknown;
interface UploadStore {
	addFile(file: File): void;
	removeFile(id: string): void;
	addError(file: File, error: string): void;
	ids: string[];
	uploads: Record<string, Upload>;

	// Folder tracking
	currentFolderId: number | null;
	setCurrentFolderId(id: number | null): void;

	// Events
	completeListeners: Set<UploadCompleteListener>;
	addCompleteListener(cb: UploadCompleteListener): EffectCallback;

	// Private methods
	_batchRunning: boolean;
	_debounceTimer: ReturnType<typeof setTimeout> | undefined;
	_scheduleWork(): void;
	_processBatch(uploads: Upload[]): Promise<void>;
}
const useUploadStore = create<UploadStore>()(
	immer((set, get) => {
		return {
			addFile(file: File) {
				set((draft) => {
					const id = crypto.randomUUID();
					draft.ids.push(id);
					draft.uploads[id] = {
						id,
						state: { status: "QUEUED" },
						file,
						controller: new AbortController(),
					};
				});

				get()._scheduleWork();
			},
			removeFile(id) {
				const upload = get().uploads[id];
				if (!upload) {
					return;
				}

				// Never abort finalize — /api/media/complete may still succeed server-side,
				// and completeListeners must run so the media library can refresh.
				const { status } = upload.state;
				if (
					status === "QUEUED" ||
					status === "REQUESTING" ||
					status === "UPLOADING"
				) {
					upload.controller.abort("User aborted upload");
				}

				set((draft) => {
					draft.ids = draft.ids.filter((x) => x !== id);
					delete draft.uploads[id];
				});

				get()._scheduleWork();
			},
			addError(file: File, error: string) {
				set((draft) => {
					const id = crypto.randomUUID();
					draft.ids.push(id);
					draft.uploads[id] = {
						id,
						state: { status: "ERROR", payload: { error } },
						file,
						controller: new AbortController(),
					};
				});
			},
			ids: [],
			uploads: {},
			currentFolderId: null,
			setCurrentFolderId(id: number | null) {
				set({ currentFolderId: id });
			},
			completeListeners: new Set(),
			addCompleteListener(cb) {
				return () => {
					set((draft) => {
						draft.completeListeners.add(cb);
					});

					return () => {
						set((draft) => {
							draft.completeListeners.delete(cb);
						});
					};
				};
			},
			_debounceTimer: undefined,
			_batchRunning: false,
			_scheduleWork() {
				// Remove work if debounce scheduled
				clearTimeout(get()._debounceTimer);
				set({ _debounceTimer: undefined });

				if (get()._batchRunning) {
					return;
				}

				const uploadArray = Object.values(get().uploads);

				const inQueue = uploadArray.filter(
					(upload) => upload.state.status === "QUEUED",
				);

				// If there are no items in queue do not try to process batch
				if (inQueue.length === 0) {
					return;
				}

				if (inQueue.length >= 3) {
					// If 3 items are in the queue, process them immediately
					get()._processBatch(inQueue.slice(0, 3));
					return;
				}

				get()._debounceTimer = setTimeout(() => {
					get()._processBatch(inQueue);
				}, 500);
			},

			/**
			 * Processing of item from the queue
			 */
			async _processBatch(batch) {
				set({ _batchRunning: true });

				// Helper function
				function updateBatch(state: UploadState) {
					set((draft) => {
						for (const upload of batch) {
							if (!draft.uploads[upload.id]) {
								continue;
							}
							draft.uploads[upload.id].state = state;
						}
					});
				}

				function update(id: string, state: UploadState) {
					set((draft) => {
						// Upload may have been dismissed from the dialog UI while finalize continues
						if (!draft.uploads[id]) {
							return;
						}
						draft.uploads[id].state = state;
					});
				}

				function getApiErrorMessage(e: unknown): string | undefined {
					if (!isAxiosError(e) || e.response?.status !== 400) {
						return undefined;
					}

					const message = e.response.data?.message;
					if (typeof message === "string") {
						return message;
					}
					if (
						message &&
						typeof message === "object" &&
						"message" in message &&
						typeof message.message === "string"
					) {
						return message.message;
					}

					return undefined;
				}

				function handleError(e: unknown, uploadID: string, generic: string) {
					if (isCancel(e)) {
						return; // No error
					}

					update(uploadID, {
						status: "ERROR",
						payload: { error: getApiErrorMessage(e) ?? generic },
					});
				}

				try {
					updateBatch({ status: "REQUESTING" });

					const folderId = get().currentFolderId;
					const { data: res } = await axios.request<
						{
							url: string;
							mediaID: number;
						}[]
					>({
						method: "POST",
						url: "/api/media/upload",
						data: batch.map((file) => ({
							filename: file.file.name,
							size: file.file.size,
							folderId,
						})),
					});

					// Initial upload state
					for (const upload of batch) {
						update(upload.id, {
							status: "UPLOADING",
							payload: {
								mediaID: res[batch.indexOf(upload)].mediaID,
								progress: 0,
							},
						});
					}

					await Promise.all(
						batch.map(async (upload) => {
							const mediaID = res[batch.indexOf(upload)].mediaID;
							const extension = upload.file.name
								.split(".")
								.pop()
								?.toLowerCase();
							const meta = findSupportedFile(upload.file.type, extension);
							const contentType =
								meta?.mime ?? upload.file.type ?? "application/octet-stream";
							try {
								await axios.request({
									method: "PUT",
									url: res[batch.indexOf(upload)].url,
									data: upload.file,
									headers: {
										"Content-Type": contentType,
										"Content-Disposition": `inline; filename*=utf-8''${encodeURIComponent(upload.file.name)}`,
									},
									onUploadProgress: (e) => {
										update(upload.id, {
											status: "UPLOADING",
											payload: {
												mediaID,
												progress: e.progress ?? 0,
											},
										});
									},
									signal: upload.controller.signal,
								});
							} catch (e) {
								handleError(e, upload.id, "Unable to upload file");
								return;
							}

							update(upload.id, {
								status: "CONFIRMING",
								payload: { mediaID },
							});

							try {
								await axios.post(
									"/api/media/complete",
									{ mediaID },
									{
										signal: upload.controller.signal,
									},
								);
							} catch (e) {
								handleError(e, upload.id, "Unable to finalize upload");
								return;
							}

							update(upload.id, {
								status: "COMPLETE",
								payload: { mediaID },
							});

							get().completeListeners.forEach((cb) => cb(mediaID));
						}),
					);
				} catch (e) {
					for (const upload of batch) {
						handleError(e, upload.id, "Unable to start upload");
					}
				} finally {
					set({ _batchRunning: false });
					get()._scheduleWork();
				}
			},
		};
	}),
);

export function useUploadList() {
	return useUploadStore(
		useShallow((store) => ({
			ids: store.ids,
			addFile: store.addFile,
			removeFile: store.removeFile,
			addError: store.addError,
		})),
	);
}

export function useSetUploadFolder() {
	return useUploadStore((store) => store.setCurrentFolderId);
}

export function useUpload(id: string) {
	return useUploadStore((store) => store.uploads[id]);
}

export function useUploadComplete(cb: UploadCompleteListener) {
	const addListener = useUploadStore((store) => store.addCompleteListener);
	const onComplete = useEventCallback(cb);

	useEffect(addListener(onComplete), []);
}
